import React, { useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { ExternalLink, FileText, Home, Languages, RefreshCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import PublicLegalNavigation from '../components/PublicLegalNavigation';
import {
  type LegalPageKey,
  type PublicLegalPageContent,
  loadPublicLegalPageContent,
  normalizePublicLegalLanguage,
  usePublicLegalConfiguration,
} from '../platform/legalConfig';
import { appStorage, runtimeConfig } from '../platform/runtime';
import { getSafeLinkAttributes } from '../platform/safeLinks';

type LegalRouteParams = {
  slug: string;
};

type LoadState = 'idle' | 'loading' | 'loaded' | 'not-found' | 'failed';

const legalPageLabelKeys: Record<LegalPageKey, string> = {
  contacts: 'legalNavigationContacts',
  legalNotice: 'legalNavigationLegalNotice',
  termsAndConditions: 'legalNavigationTermsAndConditions',
  purchaseConditions: 'legalNavigationPurchaseConditions',
  cancellationAndRefunds: 'legalNavigationCancellationAndRefunds',
  privacyPolicy: 'legalNavigationPrivacyPolicy',
  cookiesPolicy: 'legalNavigationCookiesPolicy',
  disputeResolution: 'legalNavigationDisputeResolution',
  complaintsBook: 'legalNavigationComplaintsBook',
};

const legalPageKeys = Object.keys(runtimeConfig.legal.slugs) as LegalPageKey[];
const legalLanguageOptions = [
  { value: 'pt', labelKey: 'languagePortuguese' },
  { value: 'en', labelKey: 'languageEnglish' },
  { value: 'es', labelKey: 'languageSpanish' },
  { value: 'fr', labelKey: 'languageFrench' },
];

const getLegalPageKey = (slug: string): LegalPageKey | null => {
  const normalizedSlug = slug.trim().toLowerCase();
  return legalPageKeys.find(key => runtimeConfig.legal.slugs[key] === normalizedSlug) ?? null;
};

const getAbortErrorName = (error: unknown): string | undefined =>
  typeof error === 'object' && error !== null && 'name' in error
    ? String((error as { name?: unknown }).name)
    : undefined;

const LegalPage: React.FC = () => {
  const { slug } = useParams<LegalRouteParams>();
  const { t, i18n } = useTranslation();
  const normalizedSlug = slug?.trim().toLowerCase() ?? '';
  const pageKey = useMemo(() => getLegalPageKey(normalizedSlug), [normalizedSlug]);
  const legalConfiguration = usePublicLegalConfiguration();
  const activeLanguage = normalizePublicLegalLanguage(i18n.resolvedLanguage ?? i18n.language);
  const [pageContent, setPageContent] = useState<PublicLegalPageContent | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!pageKey) {
      setPageContent(null);
      setLoadState('not-found');
      return;
    }

    const controller = new AbortController();
    let isMounted = true;

    setLoadState('loading');
    setPageContent(null);

    loadPublicLegalPageContent(normalizedSlug, activeLanguage, controller.signal)
      .then(page => {
        if (!isMounted) {
          return;
        }

        if (!page) {
          setLoadState('not-found');
          return;
        }

        setPageContent(page);
        setLoadState('loaded');
      })
      .catch(error => {
        if (!isMounted || getAbortErrorName(error) === 'AbortError') {
          return;
        }

        setLoadState('failed');
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [activeLanguage, normalizedSlug, pageKey, reloadKey]);

  const fallbackCanonicalUrl = pageKey
    ? legalConfiguration.legalPages[pageKey]
    : runtimeConfig.appUrl(`/legal/${normalizedSlug || 'pagina'}`);
  const canonicalUrl = pageContent?.canonicalUrl || fallbackCanonicalUrl;
  const title = pageContent?.title || (pageKey ? t(legalPageLabelKeys[pageKey]) : t('legalPageFallbackTitle'));

  const changeLanguage = (language: string) => {
    const nextLanguage = normalizePublicLegalLanguage(language);

    if (nextLanguage === activeLanguage) {
      return;
    }

    void i18n.changeLanguage(nextLanguage);
    appStorage.local.setItem('languageChosen', 'true');
  };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | Senti`;

    const selector = 'link[rel="canonical"][data-senti-legal-page="true"]';
    let canonicalElement = document.querySelector<HTMLLinkElement>(selector);
    const createdCanonicalElement = !canonicalElement;

    if (!canonicalElement) {
      canonicalElement = document.createElement('link');
      canonicalElement.rel = 'canonical';
      canonicalElement.dataset.sentiLegalPage = 'true';
      document.head.appendChild(canonicalElement);
    }

    canonicalElement.href = canonicalUrl;

    return () => {
      document.title = previousTitle;

      if (createdCanonicalElement) {
        canonicalElement?.remove();
      }
    };
  }, [canonicalUrl, title]);

  const retry = () => setReloadKey(current => current + 1);

  return (
    <IonPage>
      <IonContent fullscreen className="bg-stone-50">
        <div
          className="min-h-screen"
          style={{
            minHeight: '100dvh',
            paddingBottom: 'max(1rem, var(--ion-safe-area-bottom, env(safe-area-inset-bottom)))',
          }}
        >
          <header
            className="border-b border-stone-200 bg-white"
            style={{
              paddingTop: 'max(0.5rem, var(--ion-safe-area-top, env(safe-area-inset-top)))',
            }}
          >
            <div className="mx-auto flex max-w-6xl flex-col items-stretch gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase text-action-700">
                  Senti
                </p>
                <h1 className="text-xl font-semibold leading-tight text-slate-950 sm:text-2xl">
                  {t('legalPageHeaderTitle')}
                </h1>
              </div>
              <div className="flex items-center gap-2 sm:shrink-0">
                <Select value={activeLanguage} onValueChange={changeLanguage}>
                  <SelectTrigger
                    className="h-10 min-w-0 flex-1 border-stone-300 bg-white text-slate-700 sm:w-[8.75rem] sm:flex-none"
                    aria-label={t('legalPageLanguageLabel')}
                  >
                    <Languages className="mr-2 h-4 w-4 shrink-0 text-slate-500" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {legalLanguageOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  asChild
                  variant="outline"
                  className="h-10 border-stone-300 bg-white text-slate-700 hover:bg-stone-100"
                >
                  <Link to="/">
                    <Home className="h-4 w-4" />
                    App
                  </Link>
                </Button>
              </div>
            </div>
          </header>

          <main className="mx-auto grid max-w-6xl gap-5 px-4 py-5 lg:grid-cols-[minmax(0,1fr)_280px] lg:py-8">
            <article
              lang={pageContent?.language ?? 'pt'}
              data-legal-page={pageContent?.slug ?? normalizedSlug}
              data-legal-version={pageContent?.version ?? undefined}
              className="min-w-0 rounded-lg border border-stone-200 bg-white shadow-sm"
            >
              <div className="border-b border-stone-200 px-5 py-6 sm:px-8">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-action-100">
                  <FileText className="h-5 w-5 text-action-600" />
                </div>
                <p className="text-sm font-medium text-slate-500">
                  {pageContent?.lastUpdatedLabel || t('legalPageFallbackUpdated')}
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-950 sm:text-3xl">
                  {title}
                </h2>
                {pageContent?.shortDescription ? (
                  <p className="mt-3 max-w-3xl text-base leading-7 text-slate-700">
                    {pageContent.shortDescription}
                  </p>
                ) : null}
                {pageContent?.intro ? (
                  <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                    {pageContent.intro}
                  </p>
                ) : null}
              </div>

              <div className="px-5 py-6 sm:px-8">
                {loadState === 'loading' ? (
                  <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-slate-700">
                    {t('legalPageLoading')}
                  </div>
                ) : null}

                {loadState === 'failed' ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm leading-6 text-amber-900">
                        {t('legalPageLoadFailed')}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
                        onClick={retry}
                      >
                        <RefreshCcw className="h-4 w-4" />
                        {t('legalPageRetry')}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {loadState === 'not-found' ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-sm leading-6 text-red-800">
                    {t('legalPageNotFound')}
                  </div>
                ) : null}

                {loadState === 'loaded' && pageContent ? (
                  <div className="divide-y divide-stone-200">
                    {pageContent.sections.map(section => (
                      <section key={section.title} className="py-6 first:pt-0 last:pb-0">
                        <h3 className="text-lg font-semibold text-slate-950">
                          {section.title}
                        </h3>

                        <div className="mt-3 space-y-3 text-sm leading-7 text-slate-700">
                          {section.paragraphs.map(paragraph => (
                            <p key={paragraph}>{paragraph}</p>
                          ))}
                        </div>

                        {section.bullets.length > 0 ? (
                          <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-700">
                            {section.bullets.map(bullet => (
                              <li key={bullet}>{bullet}</li>
                            ))}
                          </ul>
                        ) : null}

                        {section.links.length > 0 ? (
                          <div className="mt-4 flex flex-wrap gap-2">
                            {section.links.map(link => (
                              <a
                                key={`${link.label}-${link.url}`}
                                href={link.url}
                                {...getSafeLinkAttributes(
                                  link.url,
                                  link.isExternal ? 'external' : 'internal'
                                )}
                                className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-700 focus-visible:ring-offset-2"
                              >
                                {link.label}
                                {link.isExternal ? <ExternalLink className="h-4 w-4" /> : null}
                              </a>
                            ))}
                          </div>
                        ) : null}
                      </section>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>

            <PublicLegalNavigation activePageKey={pageKey} />
          </main>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LegalPage;
